/**
 * Medición para Google Ads: conversión de compra y pasos del embudo.
 *
 * La etiqueta base (gtag.js) se carga desde public/index.html. Este módulo
 * reporta dos cosas:
 *
 *  1. La conversión de compra, cuando un pago queda confirmado (trackPurchase).
 *  2. Los pasos intermedios del embudo (trackFunnelStep), para saber DÓNDE se
 *     cae la gente entre el clic en el anuncio y el pago. Sin esto solo se
 *     conocen los extremos: cuántos entraron y cuántos pagaron.
 *
 * Todo se activa con REACT_APP_ADS_CONVERSION_LABEL, que solo está definida en
 * el build de producción. Si falta, cada función es un no-op: así el entorno de
 * desarrollo y los tests nunca ensucian los datos de la cuenta de Ads.
 *
 * Estas funciones solo observan. Ninguna modifica el flujo de la aplicación, y
 * un fallo suyo jamás debe interrumpir un registro, una cita ni un pago.
 */

const CONVERSION_ID = 'AW-18395474718';
const CONVERSION_LABEL = process.env.REACT_APP_ADS_CONVERSION_LABEL;

/**
 * Propiedad de GA4. Ambos destinos se configuran en public/index.html sobre el
 * mismo gtag.js. Google Ads sirve para pujar; GA4 es donde se analiza el
 * embudo, porque tiene informes de exploración y datos en tiempo real.
 */
const GA4_ID = 'G-RZG0P46ER0';

/** Destinos a los que se envían los eventos de embudo y de compra. */
const ALL_DESTINATIONS = [CONVERSION_ID, GA4_ID];

/**
 * Transacciones ya reportadas en esta sesión de página. Google deduplica por
 * transaction_id, pero evitamos el envío repetido en cada re-render o en cada
 * consulta del estado del pago.
 */
const reported = new Set();

const isEnabled = () =>
  Boolean(CONVERSION_LABEL) && typeof window !== 'undefined' && typeof window.gtag === 'function';

/**
 * Reporta una consulta pagada como conversión de compra.
 *
 * Solo debe llamarse con una transacción real: las citas sin costo (el
 * seguimiento gratuito dentro de 7 días) también llegan como 'completed' pero
 * no tienen transactionId, y contarlas falsearía el valor de conversión.
 *
 * @param {object}        params
 * @param {string}        params.transactionId Id de la transacción de la pasarela.
 * @param {number|string} params.value         Monto pagado.
 * @param {string}        [params.currency]    Código ISO, por defecto COP.
 * @returns {boolean} true si se envió el evento.
 */
export function trackPurchase({ transactionId, value, currency = 'COP' } = {}) {
  if (!transactionId) return false;
  if (reported.has(transactionId)) return false;

  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return false;

  if (!isEnabled()) {
    // Marcamos igualmente para que un reintento no vuelva a entrar aquí.
    reported.add(transactionId);
    return false;
  }

  // Conversión de Google Ads: exige el formato ID/etiqueta.
  window.gtag('event', 'conversion', {
    send_to: `${CONVERSION_ID}/${CONVERSION_LABEL}`,
    transaction_id: transactionId,
    value: amount,
    currency,
  });

  // El mismo pago como evento estándar de GA4, para que el embudo tenga su
  // último paso y los informes muestren ingresos. Va aparte porque el
  // `send_to` de Ads lleva la etiqueta y GA4 no la entiende.
  window.gtag('event', 'purchase', {
    send_to: GA4_ID,
    transaction_id: transactionId,
    value: amount,
    currency,
  });

  reported.add(transactionId);
  return true;
}

/**
 * Pasos del embudo, en orden. Se exportan para que las páginas no repitan
 * cadenas sueltas y para que los tests verifiquen contra la misma fuente.
 */
export const FUNNEL = {
  VIEW_LANDING: 'funnel_view_landing',        // vio /pacientes
  BEGIN_REGISTRATION: 'funnel_begin_registration', // abrió el registro
  SIGN_UP: 'funnel_sign_up',                  // completó el registro
  VIEW_SLOTS: 'funnel_view_slots',            // vio horarios (o ninguno)
  BEGIN_CHECKOUT: 'funnel_begin_checkout',    // abrió el pago
};

/**
 * Reporta un paso del embudo.
 *
 * @param {string} step   Uno de FUNNEL.
 * @param {object} [params] Datos extra del paso (ej. cuántos horarios había).
 * @returns {boolean} true si se envió el evento.
 */
export function trackFunnelStep(step, params = {}) {
  if (!step) return false;
  if (!isEnabled()) return false;

  try {
    window.gtag('event', step, { send_to: ALL_DESTINATIONS, ...params });
    return true;
  } catch {
    // La medición nunca debe romper el flujo del usuario.
    return false;
  }
}
